import "./index.scss";
import { Background, Spacers } from "types/generic/types";
import { GeneralMediumAdd } from "@wise-emotions/react-icons";
import { getBackgroundClassNames } from "utilities/commonPropTypes/background";
import { getSpacersClassNames } from "utilities/commonPropTypes/spacers";
import IconWrap from "components/Icon/IconWrap";
import React, { useEffect } from "react";
import classNames from "classnames";

export type ModalType = "" | "full-width" | "small" | "medium" | "large";

interface ModalLayoutProps extends Background, Spacers {
  alignCenter?: boolean;
  animation?: boolean;
  children: React.ReactNode;
  closeModal: VoidFunction;
  customClass?: string;
  modalType?: ModalType;
}

const ModalLayout = ({
  alignCenter = false,
  animation = true,
  backgroundColor,
  backgroundImage,
  backgroundPosition,
  backgroundSize,
  closeModal,
  children,
  customClass,
  marginBottom,
  marginTop,
  modalType = "medium",
  paddingBottom,
  paddingTop,
}: ModalLayoutProps) => {
  const modalRef = React.useRef<HTMLDivElement>(null);
  const dialogRef = React.useRef<HTMLDivElement>(null);
  const backdropRef = React.useRef<HTMLDivElement>(null);
  const closeModalOnClick = () => {
    if (animation === true && modalRef.current && backdropRef.current) {
      modalRef.current.classList.remove("show");
      backdropRef.current.classList.remove("show");
      setTimeout(() => {
        closeModal();
      }, 300);
    } else {
      closeModal();
    }
  };

  const closeModalOnClickOutsideModal = (e: React.MouseEvent) => {
    const node = dialogRef.current;
    if (node && node.contains(e.target as HTMLDivElement)) {
      return;
    }
    closeModalOnClick();
  };

  useEffect(() => {
    if (!backdropRef.current || !modalRef.current) return undefined;

    document.addEventListener(
      "mousedown",
      // @ts-expect-error
      closeModalOnClickOutsideModal,
      false,
    );
    document.body.classList.add("modal-open");
    backdropRef.current.classList.add("show");
    modalRef.current.style.display = "block";
    modalRef.current.scrollTop = 0;
    modalRef.current.classList.add("show");
    return () => {
      document.body.classList.remove("modal-open");
      document.removeEventListener(
        "mousedown",
        // @ts-expect-error
        closeModalOnClickOutsideModal,
        false,
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fadeClass = {
    fade: animation === true,
  };

  const backdropClasses = classNames(fadeClass, "modal-backdrop");
  const modalClasses = classNames(customClass, fadeClass, "modal");
  const dialogClasses = classNames("modal-dialog", {
    [`modal-dialog--${modalType}`]: modalType,
    "modal-dialog-centered": alignCenter === true,
  });
  const contentClasses = classNames(
    "modal-content",
    getBackgroundClassNames({
      backgroundColor,
      backgroundPosition,
      backgroundSize,
    }),
  );
  const contentStyle =
    backgroundImage !== ""
      ? { backgroundImage: `url(${backgroundImage})` }
      : {};
  const bodyClasses = classNames(
    "modal-body",
    getSpacersClassNames({
      marginBottom,
      marginTop,
      paddingBottom,
      paddingTop,
    }),
  );
  return (
    <>
      <div className={backdropClasses} ref={backdropRef} />
      <div className={modalClasses} tabIndex={-1} role="dialog" ref={modalRef}>
        <div className={dialogClasses} role="document" ref={dialogRef}>
          <div className={contentClasses} style={contentStyle}>
            <div className={bodyClasses}>
              {children}
              <div
                // TODO: add correct aria label ( not hardcoded value )
                aria-label="Chiudi"
                className="modal-body-close-icon"
                onClick={closeModalOnClick}
                onKeyDown={closeModalOnClick}
                role="button"
                tabIndex={0}
              >
                <IconWrap>
                  <GeneralMediumAdd />
                </IconWrap>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalLayout;
